// Constants
const DEFAULT_BATCH_SIZE = 500;
const MAX_LIMIT = 999999;

type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

type RestOptions = {
  limit?: number;
  record?: string;
  query?: string;
  offset?: number;
  method?: HttpMethod;
  data?: string;
};

type RestResults<T = Record<string, unknown>> = {
  success: boolean;
  /** For list queries: T[]. For POST/single record: raw response object (T) */
  data: T[] | T;
  message: string;
  statusCode?: number;
  length: number;
  duration: number;
  totalCount: number;
};

/**
 * Fetches data from the iMIS REST API with automatic pagination.
 *
 * @param endpoint - The API endpoint (e.g., "Party", "BOEntityDefinition")
 * @param options - Request options
 * @param options.limit - Maximum records to fetch (0 = all, default = 500)
 * @param options.record - Specific record ID for single-record operations
 * @param options.query - IQA query name
 * @param options.offset - Starting offset for pagination
 * @param options.method - HTTP method (default = "GET")
 * @param options.data - Request body for POST/PUT
 * @returns Promise with results including data array, success status, and metadata
 */
const iMISFetch = async <T = Record<string, unknown>>(
  endpoint: string,
  options: RestOptions = {}
): Promise<RestResults<T>> => {
  const startTime = performance.now();

  // Create local copy of options with defaults (don't mutate input)
  const limit = typeof options.limit !== "number" ? DEFAULT_BATCH_SIZE : options.limit === 0 ? MAX_LIMIT : options.limit;
  const method: HttpMethod = options.method ?? "GET";

  const results: RestResults<T> = {
    success: false,
    data: [],
    message: "",
    length: 0,
    duration: 0,
    totalCount: 0,
  };

  // Get verification token with null check
  const tokenElement = document.getElementById("__RequestVerificationToken") as HTMLInputElement | null;
  if (!tokenElement) {
    results.message = "Request verification token not found";
    results.duration = Math.round((performance.now() - startTime) / 1000);
    return results;
  }
  const verificationToken = tokenElement.value;

  // Build URL
  let url: URL;
  try {
    url = new URL(
      location.origin + "/API/" + endpoint
    );
    if (options.record) {
      // encodeURIComponent leaves `~` literal (iMIS expects the prefix bare)
      // but encodes `|` to %7C and `/` to %2F so composite keys like
      // `~{partyId}|CHAPT/ID` become a single percent-encoded path segment
      // instead of two segments split on the embedded `/`.
      url.pathname += "/" + encodeURIComponent(options.record);
    }
    if (options.query) {
      // `query` may be a bare IQA name ("$/ASI/Foo") or carry embedded extra
      // params from the legacy calling convention ("$/ASI/Foo&Id=123"). Split
      // on the first '&' so the extras become real URL params (otherwise
      // searchParams.append would URL-encode the '&' and iMIS would treat the
      // whole string as the queryname value).
      const ampIdx = options.query.indexOf("&");
      if (ampIdx === -1) {
        url.searchParams.append("queryname", options.query);
      } else {
        url.searchParams.append("queryname", options.query.substring(0, ampIdx));
        const extras = new URLSearchParams(options.query.substring(ampIdx + 1));
        for (const [k, v] of extras) {
          url.searchParams.append(k, v);
        }
      }
    }
  } catch {
    results.message = "Invalid URL";
    results.duration = Math.round((performance.now() - startTime) / 1000);
    return results;
  }

  // Set initial limit parameter
  const batchSize = Math.min(limit, DEFAULT_BATCH_SIZE);
  url.searchParams.append("limit", batchSize.toString());

  if (options.offset) {
    url.searchParams.append("offset", options.offset.toString());
  }

  let remaining = limit;
  let hasNext = true;

  while (hasNext && remaining > 0) {
    let fetchResponse: Response;
    try {
      fetchResponse = await fetch(url.toString(), {
        method,
        body: options.data,
        headers: {
          "content-type": "application/json",
          RequestVerificationToken: verificationToken,
        },
      });
    } catch (error) {
      results.message = error instanceof Error ? error.message : "Network error";
      results.duration = Math.round((performance.now() - startTime) / 1000);
      return results;
    }

    if (!fetchResponse.ok) {
      results.message = await fetchResponse.text();
      results.statusCode = fetchResponse.status;
      results.duration = Math.round((performance.now() - startTime) / 1000);
      return results;
    }

    results.success = true;
    results.statusCode = fetchResponse.status;

    // Handle DELETE - no response body expected
    if (method === "DELETE") {
      results.length = 0;
      results.totalCount = 0;
      results.data = [];
      break;
    }

    let responseJSON: Record<string, unknown>;
    try {
      responseJSON = await fetchResponse.json();
    } catch {
      results.message = "Failed to parse JSON response";
      results.success = false;
      results.duration = Math.round((performance.now() - startTime) / 1000);
      return results;
    }

    // Handle single record or non-GET response (returns raw object, no pagination)
    if (options.record || method === "POST" || method === "PUT") {
      results.data = responseJSON as unknown as T[];
      results.length = 1;
      results.totalCount = 1;
      break;
    }

    // Handle paginated list response
    hasNext = responseJSON.HasNext as boolean;
    results.totalCount = responseJSON.TotalCount as number;
    results.length += responseJSON.Count as number;

    const items = (responseJSON.Items as { $values: T[] })?.$values ?? [];
    results.data = (results.data as T[]).concat(items);

    remaining = limit - results.length;
    if (remaining < DEFAULT_BATCH_SIZE) {
      url.searchParams.set("limit", remaining.toString());
    }
    url.searchParams.set("offset", (responseJSON.NextOffset as number).toString());
  }

  results.duration = Math.round((performance.now() - startTime) / 1000);
  return results;
};

export default iMISFetch;
export type { RestOptions, RestResults, HttpMethod };
